import { useEffect, useRef } from 'react';
import OneSignal from 'react-onesignal';
import { isAndroidORIosApp } from 'webtonative';
import * as nativeOneSignal from 'webtonative/OneSignal';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

/**
 * OneSignalProvider (iter69 — aggressive external_id sync)
 *
 * Why this version is so defensive:
 *   In iter66 we relied on react-onesignal's `OneSignal.login()` finishing
 *   synchronously after init. In production this race manifests for some
 *   users as "External ID stays blank in OneSignal Dashboard" even though
 *   broadcasts work fine.
 *
 * iter69 strategy:
 *   1. Wait for init promise to actually resolve.
 *   2. Poll up to 20s for OneSignal.User.PushSubscription.id to become non-null
 *      (this signals the subscription has been registered with OneSignal cloud).
 *   3. Once a subscription exists AND the user is signed in, call BOTH
 *      `OneSignal.login(externalId)` AND `OneSignal.User.addAlias('external_id', externalId)`
 *      defensively — different SDK versions handle one but not the other.
 *   4. Verify by reading `OneSignal.User.externalId` and retry up to 5 times if blank.
 *   5. Loud `console.info('[OneSignal]', ...)` logs at every step so the operator
 *      can paste browser-console output for debugging.
 *   6. Listen to `permissionChange` and `subscription.change` events to re-tag
 *      when the user grants permission after sign-in.
 */
const OneSignalProvider = ({ children }) => {
  const initPromiseRef = useRef(null);
  const lastExternalIdRef = useRef(null);
  const { user } = useAuth();

  // One-time SDK init
  useEffect(() => {
    if (initPromiseRef.current) return;
    // Inside the WebToNative Android/iOS wrapper, the Web Push API is NOT
    // available (the WebView doesn't implement it). OneSignal subscriptions
    // are registered by the NATIVE OneSignal SDK that WebToNative bundles
    // via its OneSignal plugin. We must NOT initialize react-onesignal here
    // — its slidedown prompt and service-worker registration would fail
    // silently AND produce "Notifications not supported" toasts.
    if (isAndroidORIosApp) {
      // eslint-disable-next-line no-console
      console.info('[OneSignal] native app detected — skipping web SDK init; identity sync will use webtonative/OneSignal bridge');
      // Mark init as resolved so the second effect runs immediately.
      initPromiseRef.current = Promise.resolve();
      return;
    }

    const appId = process.env.REACT_APP_ONESIGNAL_APP_ID;
    if (!appId) return;

    // eslint-disable-next-line no-console
    console.info('[OneSignal] init starting…', { appId });

    initPromiseRef.current = OneSignal.init({
      appId,
      serviceWorkerParam: { scope: '/onesignal/' },
      serviceWorkerPath: '/onesignal/OneSignalSDKWorker.js',
      // iter69-fix: autoPrompt was previously FALSE which meant new users
      // were never asked for notification permission → no subscription →
      // no external_id → chat targeting failed. Now we auto-show the
      // slidedown shortly after the first page view so opted-in users get
      // a real PushSubscription.id and chat notifications can fire.
      promptOptions: {
        slidedown: {
          prompts: [
            {
              type: 'push',
              autoPrompt: true,
              text: {
                actionMessage: 'Get instant alerts for new chat messages, offers, and listing updates from GenzoX.',
                acceptButton: 'Enable Notifications',
                cancelButton: 'Not now',
              },
              delay: { pageViews: 1, timeDelay: 5 },
            },
          ],
        },
      },
      allowLocalhostAsSecureOrigin: true,
    }).then(() => {
      console.info('[OneSignal] init resolved');
      // iter89 — Web push notification-click deep-link handler.
      // OneSignal fires this listener when the user clicks a notification
      // in the browser (Chrome/Edge/Firefox on desktop, PWA on Android).
      // Native iOS/Android clicks are routed via WebToNative's own bridge
      // and handled below in the native branch. The additionalData payload
      // carries our `conversation_id` when the push was sent by
      // /api/messages → onesignal_client.push_to_external_ids(...data=...).
      try {
        if (OneSignal.Notifications && typeof OneSignal.Notifications.addEventListener === 'function') {
          OneSignal.Notifications.addEventListener('click', (event) => {
            const info = event?.notification || event || {};
            const dataObj = info.additionalData || info.data || {};
            const convId = dataObj.conversation_id || dataObj.conversationId;
            const rawUrl = info.launchURL || info.url || dataObj.url || '';
            let target = '/';
            if (convId) target = `/chat/${convId}`;
            else if (rawUrl) {
              // Only honour same-origin paths to prevent open-redirect abuse.
              try {
                const u = new URL(rawUrl, window.location.origin);
                if (u.origin === window.location.origin) target = u.pathname + u.search;
              } catch (_) { /* keep default */ }
            }
            console.info('[OneSignal] click → navigating to', target, 'raw payload:', dataObj);
            // History-push so React Router picks it up without a full reload,
            // AND emit a popstate so route-listeners react. If the app isn't
            // loaded (browser was closed), the notification click launches the
            // page fresh and our Homepage/ChatPage handles the target URL.
            window.location.href = target;
          });
        }
      } catch (clickErr) {
        console.warn('[OneSignal] click listener attach failed:', clickErr);
      }
    }).catch((err) => {
      console.error('[OneSignal] init failed:', err);
    });
  }, []);

  // Aggressive identity sync — see file-header comment for strategy.
  useEffect(() => {
    const initPromise = initPromiseRef.current;
    if (!initPromise) return;
    const targetId = user?.user_id || null;
    let cancelled = false;

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const waitForSubscriptionId = async (timeoutMs = 20000) => {
      const deadline = Date.now() + timeoutMs;
      while (Date.now() < deadline) {
        const subId = OneSignal?.User?.PushSubscription?.id;
        if (subId) return subId;
        await sleep(500);
        if (cancelled) return null;
      }
      return null;
    };

    const setExternalIdWithRetry = async (externalId) => {
      for (let attempt = 1; attempt <= 5; attempt++) {
        if (cancelled) return false;
        try {
          await OneSignal.login(externalId);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[OneSignal] login() failed attempt', attempt, err);
        }
        try {
          OneSignal.User?.addAlias?.('external_id', externalId);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[OneSignal] addAlias failed attempt', attempt, err);
        }
        await sleep(800);
        const observed = OneSignal?.User?.externalId;
        // eslint-disable-next-line no-console
        console.info('[OneSignal] external_id after attempt', attempt, '→', observed);
        if (observed && observed === externalId) return true;
      }
      return false;
    };

    const syncIdentity = async (reason) => {
      try {
        await initPromise;
        if (cancelled) return;

        // ── Native app branch ──────────────────────────────────────────
        // Inside the WebToNative wrapper, identity is tagged via the
        // OneSignal native Android/iOS SDK (NOT react-onesignal). Steps:
        //   1. Call setExternalUserId(userId) — bridges to native SDK
        //   2. Poll for getPlayerId() (= OneSignal subscription_id) which
        //      becomes available once the native SDK has finished
        //      registering with OneSignal cloud.
        //   3. POST that subscription_id to /api/onesignal/link as a
        //      server-side safety net (same pattern as web).
        if (isAndroidORIosApp) {
          if (!targetId) {
            if (lastExternalIdRef.current) {
              // eslint-disable-next-line no-console
              console.info('[OneSignal] native logout (was:', lastExternalIdRef.current, ')');
              try { nativeOneSignal.removeExternalUserId(); } catch (_e) { /* native SDK may not be ready */ }
              lastExternalIdRef.current = null;
            }
            return;
          }

          // eslint-disable-next-line no-console
          console.info('[OneSignal] native syncIdentity reason:', reason, 'target:', targetId);
          // ── iter83 fix ── Explicitly opt the user in FIRST. On iOS this
          // triggers the system-level notification permission prompt via
          // the native OneSignal iOS SDK; without this call, iOS users
          // never see the prompt → never get a player_id → server pushes
          // reach 0 recipients. Safe to call on Android too (idempotent).
          try {
            nativeOneSignal.optInUser?.();
          } catch (_e) { /* SDK may still be booting — safe to ignore */ }
          try {
            nativeOneSignal.setExternalUserId(targetId);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[OneSignal] native setExternalUserId failed:', err);
          }
          lastExternalIdRef.current = targetId;

          // ── iter83 fix ── Extended player-id acquisition window.
          // Previously we polled for 20s then gave up if playerId was null.
          // Users who granted permission after the 20s window (e.g. read the
          // prompt slowly, or the iOS SDK cold-boot took longer than usual)
          // would silently miss registration — resulting in "notifications
          // never arrive" even though everything else is configured correctly.
          //
          // New strategy: poll up to 60s in the foreground; on failure,
          // schedule 3 more background retries at 60s / 3min / 10min so a
          // late permission grant eventually gets the player_id linked
          // without requiring the user to relaunch the app.
          const pollForPlayerId = async (windowMs) => {
            const deadline = Date.now() + windowMs;
            while (Date.now() < deadline) {
              try {
                const pid = await nativeOneSignal.getPlayerId();
                if (pid) return pid;
              } catch (_e) { /* ignore — SDK may not be ready */ }
              await sleep(1500);
              if (cancelled) return null;
            }
            return null;
          };

          const registerPlayerIdWithBackend = async (playerId) => {
            if (!playerId) return false;
            try {
              await api.post('/onesignal/link', { subscription_id: playerId });
              // eslint-disable-next-line no-console
              console.info('[OneSignal] ✓ native external_id ↔ player_id linked with backend:', playerId);
              return true;
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn('[OneSignal] backend link request failed:', err?.response?.status, err?.message);
              return false;
            }
          };

          let playerId = await pollForPlayerId(60000);
          if (playerId) {
            await registerPlayerIdWithBackend(playerId);
          } else {
            // eslint-disable-next-line no-console
            console.warn('[OneSignal] native playerId not available after 60s — scheduling background retries');
            // 3 fire-and-forget retries so the user gets registered even if
            // they granted notification permission after the initial window.
            for (const delay of [60000, 180000, 600000]) {
              setTimeout(async () => {
                if (cancelled) return;
                if (lastExternalIdRef.current !== targetId) return; // user changed
                const late = await pollForPlayerId(15000);
                if (late) await registerPlayerIdWithBackend(late);
              }, delay);
            }
          }
          return;
        }
        // ── End native app branch ─────────────────────────────────────

        if (!targetId) {
          if (lastExternalIdRef.current) {
            // eslint-disable-next-line no-console
            console.info('[OneSignal] logout (was:', lastExternalIdRef.current, ')');
            await OneSignal.logout().catch(() => {});
            lastExternalIdRef.current = null;
          }
          return;
        }

        // eslint-disable-next-line no-console
        console.info('[OneSignal] syncIdentity reason:', reason, 'target:', targetId);

        // iter69-fix: If the user is signed in but has NOT granted notification
        // permission yet, the slidedown prompt would be the next step. We also
        // proactively call requestPermission() — on Chrome (desktop + Android +
        // WebToNative wrapper) this surfaces the native permission dialog.
        // (Safari iOS needs the site installed as a PWA first; this no-ops there.)
        try {
          const perm = OneSignal.Notifications?.permission;
          // eslint-disable-next-line no-console
          console.info('[OneSignal] current permission:', perm);
          if (perm === false || perm === 'default') {
            OneSignal.Notifications?.requestPermission?.().catch(() => {});
          }
        } catch (_e) { /* permission probing optional */ }

        // Wait for a subscription to exist — without one, the external_id has
        // nothing to attach to and the alias is effectively dropped.
        const subId = await waitForSubscriptionId(20000);
        if (cancelled) return;
        if (!subId) {
          // eslint-disable-next-line no-console
          console.warn('[OneSignal] no PushSubscription.id after 20s — user has not granted permission yet?');
          // Still try login so when permission is granted later the identity is staged
          await OneSignal.login(targetId).catch(() => {});
          return;
        }

        // eslint-disable-next-line no-console
        console.info('[OneSignal] subscription ready, id:', subId);

        const ok = await setExternalIdWithRetry(targetId);
        if (ok) {
          lastExternalIdRef.current = targetId;
          // eslint-disable-next-line no-console
          console.info('[OneSignal] ✓ external_id set successfully:', targetId);
        } else {
          // eslint-disable-next-line no-console
          console.error('[OneSignal] ✗ external_id sync FAILED after 5 attempts. target:', targetId, 'observed:', OneSignal?.User?.externalId);
        }

        // iter69 — Server-side safety net. Even when client SDK appears to have
        // succeeded we ALSO hit the backend with the subscription_id so it can
        // PATCH the OneSignal identity via REST API. Idempotent on OneSignal's
        // end so doing both is safe; this guarantees external_id is set even
        // if the SDK quietly failed.
        try {
          await api.post('/onesignal/link', { subscription_id: subId });
          // eslint-disable-next-line no-console
          console.info('[OneSignal] backend link request sent for subscription', subId);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[OneSignal] backend link request failed:', err?.response?.status, err?.message);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[OneSignal] syncIdentity threw:', err);
      }
    };

    syncIdentity('auth-change');

    // Web-only event listeners — react-onesignal isn't initialized in native
    // app mode, so attaching listeners would throw.
    const onPermissionChange = () => syncIdentity('permission-change');
    const onSubscriptionChange = () => syncIdentity('subscription-change');
    if (!isAndroidORIosApp) {
      try {
        OneSignal.Notifications?.addEventListener?.('permissionChange', onPermissionChange);
        OneSignal.User?.PushSubscription?.addEventListener?.('change', onSubscriptionChange);
      } catch (_e) { /* listeners optional */ }
    }

    return () => {
      cancelled = true;
      if (!isAndroidORIosApp) {
        try {
          OneSignal.Notifications?.removeEventListener?.('permissionChange', onPermissionChange);
          OneSignal.User?.PushSubscription?.removeEventListener?.('change', onSubscriptionChange);
        } catch (_e) { /* listener cleanup is best-effort */ }
      }
    };
  }, [user]);

  return children;
};

export default OneSignalProvider;
